import type { BankKey } from './types.ts';

function inferredSlope(keys: readonly BankKey[], index: number): number {
  if (keys[index].slope !== undefined) return keys[index].slope;
  if (keys.length < 2) return 0;
  const before = keys[Math.max(0, index - 1)];
  const after = keys[Math.min(keys.length - 1, index + 1)];
  const span = after.distance - before.distance;
  return span > 1e-9 ? (after.value - before.value) / span : 0;
}

export function evaluateBank(keysInput: readonly BankKey[] | undefined, distance: number): number {
  if (!keysInput?.length) return 0;
  const keys = [...keysInput].sort((a, b) => a.distance - b.distance);
  if (distance <= keys[0].distance) return keys[0].value;
  if (distance >= keys[keys.length - 1].distance) return keys[keys.length - 1].value;
  let low = 0;
  let high = keys.length - 1;
  while (high - low > 1) {
    const middle = (low + high) >> 1;
    if (keys[middle].distance <= distance) low = middle;
    else high = middle;
  }
  const first = keys[low];
  const second = keys[high];
  const span = second.distance - first.distance;
  if (!(span > 1e-9)) return second.value;
  const t = (distance - first.distance) / span;
  const t2 = t * t;
  const t3 = t2 * t;
  const h00 = 2 * t3 - 3 * t2 + 1;
  const h10 = t3 - 2 * t2 + t;
  const h01 = -2 * t3 + 3 * t2;
  const h11 = t3 - t2;
  return (
    h00 * first.value +
    h10 * span * inferredSlope(keys, low) +
    h01 * second.value +
    h11 * span * inferredSlope(keys, high)
  );
}
